import config from 'constants/config'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import { useMemo } from 'react'

import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import useIsFlightsDisabledForGeoState from 'hooks/Flights/useIsFlightsDisabledForGeoState'
import { getAgentHubAffiliationRules } from 'agentHub/selectors/agentHubSelectors'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import { isEnglishLocale } from 'i18n/util/locale'
import useSearchVerticalLabels from './useSearchVerticalLabels'

/**
 * Returns the list of enabled search verticals for the current brand
 */
function useGlobalSearchVerticals(): Array<App.SearchVerticalItem> {
  // TODO: this should be single selector @arminmon
  const isFlightsDisabledForGeoState = useIsFlightsDisabledForGeoState()
  const standaloneFlightsEnabled = useAppSelector((state) => isStandaloneFlightsEnabled(state) || isFlightsCredit(state)) && !isFlightsDisabledForGeoState
  const agentHubAffiliationRules = useAppSelector((state) => getAgentHubAffiliationRules(state))
  const isShopEnabled = useAppSelector((state) => getIsMerchandiseEnabled(state))
  const locale = useAppSelector((state) => state.geo.locale)

  const canBookCruise = !isAgentHubEnabled || agentHubAffiliationRules.canBookCruise
  const canBookCarHire = !isAgentHubEnabled || agentHubAffiliationRules.canBookCarHire

  const toursEnabled = useTourV2SearchEnabled()

  const regionCode = useAppSelector((state) => state.geo.currentRegionCode)
  const isInAvailableCruiseRegion = config.CRUISES_REGIONS.split(',').includes(regionCode)
  const isInAvailableCarHireRegion = config.CARHIRE_REGIONS.split(',').includes(regionCode)

  const verticalLabels = useSearchVerticalLabels()

  return useMemo(() => {
    const enabledSearchVerticals = {
      'car-hire': config.CARHIRE_ENABLED && config.CARHIRE_SEARCH_ENABLED && isInAvailableCarHireRegion && canBookCarHire,
      cruises: config.CRUISES_STANDLONE_ENABLED && isInAvailableCruiseRegion && canBookCruise,
      experiences: config.EXPERIENCES_STANDLONE_ENABLED,
      flights: standaloneFlightsEnabled,
      hotels: true,
      tours: toursEnabled,
      'homes-and-villas': config.RENTALS_SEARCH_ENABLED,
      merchandise: isShopEnabled && isEnglishLocale(locale),
    }

    return config.searchVerticalItems
      .filter((searchVerticalItem) => enabledSearchVerticals[searchVerticalItem.vertical])
      .map((searchVerticalItem) => ({
        ...searchVerticalItem,
        label: verticalLabels[searchVerticalItem.vertical],
      }))
  }, [isInAvailableCarHireRegion, isInAvailableCruiseRegion, standaloneFlightsEnabled, toursEnabled, canBookCarHire, canBookCruise, isShopEnabled, locale, verticalLabels])
}

export default useGlobalSearchVerticals
